import { createFileRoute } from "@tanstack/react-router";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/legal/disclaimers";

export const Route = createFileRoute("/legal/disclaimers")({
  head: () =>
    buildHead({
      title: "Disclaimers | Simply Approved Mortgages",
      description:
        "Important disclaimers about DSCR and business-purpose loan estimates, calculators, AI-assisted content, and this website.",
      url: URL,
      keywords:
        "DSCR loan disclaimer, business purpose lending disclaimer, not a commitment to lend, calculator disclaimer, mortgage broker disclaimer",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Disclaimers", url: URL },
      ],
      localBusiness: false,
    }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Legal"
        title="Disclaimers"
        subtitle="Important disclosures about our loan estimates, pricing tools, calculators, and this website."
      />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <PageDatesBadge className="not-prose mb-4" />

          <h2>Government affiliation</h2>
          <p>
            {SITE.brand} is not affiliated with, endorsed by, or acting on behalf of the U.S.
            Department of Housing and Urban Development (HUD), the Federal Housing Administration
            (FHA), the Consumer Financial Protection Bureau (CFPB), Fannie Mae, Freddie Mac, or any
            other government agency. HUD data reproduced on this site (such as Fair Market Rents,
            income limits, or census-tract designations) is published by HUD and is used for
            educational purposes only.
          </p>

          <h2>Broker, not a lender</h2>
          <p>
            Simply Approved Mortgages LLC (NMLS #2620881) is a licensed mortgage <em>brokerage</em>,
            not a direct lender. Loans described on this site are originated, underwritten, and
            funded by third-party wholesale lenders and investors. Wholesale lender names, rate
            sheets, and credit matrices are not published on this site.
          </p>

          <h2>Not an offer of credit</h2>
          <p>
            Nothing on this website is a commitment to lend or an offer of credit. All financing is
            subject to a completed application, credit and property review, appraisal where
            required, verification of information, lender requirements, and full underwriting.
            Rates, fees, leverage limits, program guidelines, and product availability change
            without notice.
          </p>

          <h2>Business-purpose lending only</h2>
          <p>
            DSCR, HELOC, bridge, fix-and-flip, construction, portfolio, and the other programs
            described on this site are business-purpose loans secured by non-owner-occupied
            investment property. They are not available for owner-occupied, primary-residence,
            second-home, or personal, family, or household purposes, and are generally exempt from
            TILA/RESPA consumer-mortgage disclosure requirements. Requests submitted through this
            site are pre-qualification requests, not loan applications under TILA/RESPA, and do not
            trigger a Loan Estimate.
          </p>

          <h2>Borrower obligations</h2>
          <p>
            Borrowers remain responsible for property taxes, insurance, HOA dues, maintenance,
            required reserves, and all other obligations under the loan documents. Failure to meet
            those obligations may result in default and foreclosure. Investment property carries
            risk, including vacancy, tenant non-payment, expense increases, and changes in property
            value.
          </p>

          <h2>Calculators, pricing tools, and estimates</h2>
          <p>
            Our DSCR pricing tool, HELOC pricing tool, and investor calculators (DSCR, BRRRR,
            cap rate, cash-on-cash, ARV, and others) are educational, illustrative planning tools
            based on the inputs you provide and modeled assumptions. Results are not quotes,
            approvals, appraisals, rent certifications, or commitments to lend, and no figure shown
            should be relied upon as a promise. Rent, value, and market data drawn from third-party
            or public sources are estimates, not appraisals or lease verifications.
          </p>

          <h2>Rate and pricing displays</h2>
          <p>
            Any rate, payment, or pricing scenario shown reflects a specific set of assumptions at a
            specific point in time and is subject to change without notice. Payment figures shown
            are principal and interest only and exclude taxes, insurance, HOA dues, and other
            escrowed or property-level costs. Your actual pricing depends on credit, property,
            occupancy, lease status, leverage, reserves, entity structure, state, and lender
            guidelines.
          </p>

          <h2>Not tax, legal, or investment advice</h2>
          <p>
            Information on this site is educational and general in nature. It is not tax, legal,
            accounting, or investment advice, and no content here is a recommendation to buy, sell,
            or hold any property or security. Consult a qualified tax advisor, attorney, and
            licensed professional about your specific situation.
          </p>

          <h2>Examples and scenarios</h2>
          <p>
            Any deal examples, case studies, or scenarios on this site are illustrative. Your
            results will depend on your property, market, credit profile, lease terms, and the
            program you qualify for.
          </p>

          <h2>Third-party content</h2>
          <p>
            Links to third-party websites (HUD, CFPB, NMLS Consumer Access, data providers, and
            others) are provided as a convenience. We do not endorse and are not responsible for
            content on those sites.
          </p>

          <h2>Consent to be contacted (TCPA)</h2>
          <p>
            When you submit our pre-qualification form, a calculator lead form, or any contact form,
            you provide <strong>express written consent</strong> for {SITE.brand} and its agents to
            contact you at the phone number(s) and email address(es) you provide — including your
            wireless number — by live call, prerecorded or artificial voice, SMS/MMS text, email,
            and AI-assisted voice, chat, and follow-up, even if your number is on a Do Not Call
            registry. Consent is not a condition of any purchase or of obtaining credit. You may opt
            out at any time by replying <strong>STOP</strong> to SMS, clicking{" "}
            <strong>unsubscribe</strong> in any email, using our <a href="/contact">contact form</a>,
            or calling <a href={SITE.phoneHref}>{SITE.phone}</a>. Message and data rates may apply.
            See our <a href="/legal/privacy">Privacy Policy</a> for details.
          </p>

          <h2>AI-assisted content and human review</h2>
          <p>
            Portions of this site, including our chat assistant and some written content, may be
            produced or assisted by artificial intelligence. AI tools can make mistakes, produce
            outdated figures, or misunderstand your situation. AI output is not a source of truth,
            is not a representation or promise by {SITE.brand}, and is never a substitute for a
            licensed loan officer or qualified professional. Material AI-assisted content is held
            for human review before publication, but human review is a good-faith process, not a
            guarantee. The only reliable figures are those provided to you directly in writing by a
            licensed loan officer.
          </p>

          <h2>Use at your own risk</h2>
          <p>
            Your use of this site and its tools is at your sole risk. All content is provided "as
            is" and "as available" without warranties of any kind. Nothing on this site creates a
            client, advisory, or lender-borrower relationship, and decisions made in reliance on
            this site are made at your own discretion and risk.
          </p>

          <h2>Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, Simply Approved Mortgages LLC and its officers,
            employees, agents, and affiliates shall not be liable for any indirect, incidental,
            special, consequential, exemplary, or punitive damages, or any loss of profits, revenue,
            data, or use, arising out of or relating to your use of this site, our tools, or any
            information provided — including errors, inaccuracies, or omissions in any content,
            calculator output, estimate, or AI-assisted material, whether or not reviewed — whether
            based in contract, tort, strict liability, negligence, or otherwise. Your sole remedy
            for dissatisfaction with this site is to stop using it. Some jurisdictions do not allow
            certain limitations, so portions of this limitation may not apply to you.
          </p>

          <h2>Equal Housing Opportunity</h2>
          <p>
            {SITE.brand} is an Equal Housing Opportunity brokerage. We do business in accordance with
            the Federal Fair Housing Act and the Equal Credit Opportunity Act.
          </p>

          <h2>Contact us</h2>
          <ul>
            <li>Phone: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
            <li>Online: <a href="/contact">contact form</a></li>
            <li>Business phone: <a href={SITE.phoneHref}>{SITE.phone}</a></li>
          </ul>

          <LicensingDisclosure className="not-prose mt-6 text-sm text-muted-foreground" />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
